import { useEffect, useState } from 'react'
import {
  LIBELLE_MODE_CLE, cleActive, decrireJournalApi, libelleAccesApi, libelleAuteurApi, messageAccesApi,
  prefixeCle, validerNoteAcces,
  type CleApi, type EntreeJournalApi,
} from '../api/accesApi'
import { dateHeure, dateLongue } from '../api/format'
import type { Organisation, Source } from '../api/types'
import { Badge } from './Badges'
import { Icone } from './Icone'
import { Modale } from './Modale'

export type Chargement<T> =
  | { etat: 'chargement' }
  | { etat: 'erreur'; message: string }
  | { etat: 'pret'; lignes: T[] }

/** Charge une liste à l'ouverture et à chaque changement de `cles` ; une lecture périmée est ignorée. */
export function useListe<T>(charge: () => Promise<T[]>, cles: readonly unknown[]): [Chargement<T>, () => void] {
  const [etat, setEtat] = useState<Chargement<T>>({ etat: 'chargement' })
  const [essai, setEssai] = useState(0)
  useEffect(() => {
    let annule = false
    setEtat({ etat: 'chargement' })
    charge().then(
      (lignes) => { if (!annule) setEtat({ etat: 'pret', lignes }) },
      (e: unknown) => { if (!annule) setEtat({ etat: 'erreur', message: messageAccesApi(e) }) },
    )
    return () => { annule = true }
  }, [...cles, essai])
  return [etat, () => setEssai((n) => n + 1)]
}

/** Les deux drapeaux d'une organisation ou d'un client API. */
export interface Drapeaux { sandbox: boolean; production: boolean }

/** Ce qu'on envoie pour poser les drapeaux : les deux valeurs et la raison (déjà validée). */
export type EnregistrerAcces = (m: Drapeaux & { note: string }) => Promise<void>

/**
 * « Accès API » d'une organisation (cas b : transporteur avec sa flotte) : les deux drapeaux (test, production),
 * les clés et le journal. L'offre API est DISTINCTE du SaaS : seuls les administrateurs de la plateforme ouvrent
 * ou ferment un accès (`platform_definir_acces_api`), toujours avec une raison, journalisée avec leur nom.
 * L'organisation opératrice a l'accès d'office : ses clients API se gèrent dans l'onglet « Clients API ».
 */
export function SectionAccesApi({
  org,
  source,
  surChange,
  notifier,
}: {
  org: Organisation
  source: Source
  /** Recharge les données de la console (le tableau montre l'accès de chaque organisation). */
  surChange: () => Promise<void>
  notifier: (m: string) => void
}) {
  const [version, setVersion] = useState(0)
  const [changement, setChangement] = useState<Drapeaux | null>(null)
  const [aRevoquer, setARevoquer] = useState<CleApi | null>(null)
  const [cles, rechargerCles] = useListe(() => source.clesApi(org.org_id, null), [source, org.org_id, version])
  const [journal, rechargerJournal] = useListe(() => source.journalAccesApi(org.org_id, null), [source, org.org_id, version])

  const nom = org.brand_name || org.name
  const acces = org.acces_api
  const actuel: Drapeaux | null = acces ? { sandbox: acces.api_sandbox, production: acces.api_production } : null

  /** Après une écriture réussie : relit les clés, le journal et le tableau. `surChange` ne lève jamais. */
  async function apresEcriture(message: string) {
    setVersion((v) => v + 1)
    notifier(message)
    await surChange()
  }

  return (
    <>
      <h3 className="fiche-titre">Accès API</h3>
      {org.is_operator ? (
        <p className="encart">
          <Icone nom="lock" /> Accès d'office — ses clients API se gèrent dans l'onglet Clients API.
        </p>
      ) : !acces || !actuel ? (
        <p className="encart encart-alerte" role="status">
          <Icone nom="alert" /> L'accès API n'est pas lisible pour le moment (migration 0048 à pousser ?).
        </p>
      ) : (
        <>
          <dl className="liste-def">
            <div><dt>Accès actuel</dt><dd>{libelleAccesApi(acces)}</dd></div>
            <div><dt>Clés actives (test / production)</dt><dd className="n">{acces.cles_test_actives} / {acces.cles_live_actives}</dd></div>
            <div><dt>Dernière utilisation</dt><dd>{acces.derniere_utilisation ? dateHeure(acces.derniere_utilisation) : '—'}</dd></div>
            <div><dt>Dernier changement</dt><dd>{acces.dernier_changement ? dateHeure(acces.dernier_changement) : '—'}</dd></div>
          </dl>
          <InterrupteursAcces actuel={actuel} surChoix={setChangement} />
        </>
      )}

      <TableCles etat={cles} recharger={rechargerCles} surRevoquer={setARevoquer} vide="Aucune clé créée pour cette organisation." />
      <JournalAcces etat={journal} recharger={rechargerJournal} />

      {changement && actuel && (
        <ModaleChangementAcces
          nom={nom}
          avant={actuel}
          apres={changement}
          enregistrer={(m) => source.definirAccesApi({ org: org.org_id, ...m })}
          surFermer={() => setChangement(null)}
          surFait={() => apresEcriture(`Accès API de « ${nom} » mis à jour`)}
        />
      )}
      {aRevoquer && (
        <ModaleRevocation
          cle={aRevoquer}
          nom={nom}
          revoquer={(note) => source.revoquerCleApi(aRevoquer.id, note)}
          surFermer={() => setARevoquer(null)}
          surFait={() => apresEcriture(`Clé « ${aRevoquer.label} » révoquée`)}
        />
      )}
    </>
  )
}

/** Les deux interrupteurs (test, production) : un clic propose le nouvel état, la confirmation l'enregistre. */
export function InterrupteursAcces({ actuel, surChoix }: { actuel: Drapeaux; surChoix: (apres: Drapeaux) => void }) {
  return (
    <div className="acces-api">
      <label className="interrupteur">
        <input
          type="checkbox" role="switch" checked={actuel.sandbox}
          onChange={() => surChoix({ ...actuel, sandbox: !actuel.sandbox })}
        />
        <span className="piste" aria-hidden="true"><span className="curseur" /></span>
        <span className="interrupteur-texte">
          <b>Accès de test (bac à sable)</b>
          <span className="aide">Clés sk_test_ et documentation.</span>
        </span>
      </label>
      <label className="interrupteur">
        <input
          type="checkbox" role="switch" checked={actuel.production}
          onChange={() => surChoix({ ...actuel, production: !actuel.production })}
        />
        <span className="piste" aria-hidden="true"><span className="curseur" /></span>
        <span className="interrupteur-texte">
          <b>Accès de production</b>
          <span className="aide">Clés sk_live_ : de vraies courses, de vrais clients.</span>
        </span>
      </label>
    </div>
  )
}

/** La liste des clés (jamais d'empreinte) avec « Révoquer » sur les clés actives. */
export function TableCles({
  etat, recharger, surRevoquer, vide,
}: {
  etat: Chargement<CleApi>
  recharger: () => void
  surRevoquer: (c: CleApi) => void
  vide: string
}) {
  return (
    <>
      <h3 className="fiche-titre">Clés API</h3>
      {etat.etat === 'chargement' && <div className="sk" aria-busy="true" aria-label="Chargement des clés" />}
      {etat.etat === 'erreur' && (
        <p className="encart encart-alerte" role="alert">
          <Icone nom="alert" /> {etat.message}{' '}
          <button type="button" className="lien" onClick={recharger}>Réessayer</button>
        </p>
      )}
      {etat.etat === 'pret' && etat.lignes.length === 0 && <p className="discret-t">{vide}</p>}
      {etat.etat === 'pret' && etat.lignes.length > 0 && (
        <div className="defile">
          <table>
            <thead>
              <tr>
                <th>Libellé</th><th>Préfixe</th><th>Mode</th><th>Créée le</th><th>Dernier usage</th><th>État</th>
                <th><span className="sr">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {etat.lignes.map((c) => (
                <tr key={c.id}>
                  <td className="fort">{c.label}</td>
                  <td className="n">{prefixeCle(c)}</td>
                  <td><Badge classe={c.mode === 'live' ? 'em' : 'info'}>{LIBELLE_MODE_CLE[c.mode]}</Badge></td>
                  <td className="discret-t">{dateLongue(c.created_at)}</td>
                  <td className="discret-t">{c.last_used_at ? dateHeure(c.last_used_at) : 'jamais'}</td>
                  <td><Badge classe={cleActive(c) ? 'vert' : 'gris'}>{cleActive(c) ? 'Active' : 'Révoquée'}</Badge></td>
                  <td>
                    {cleActive(c) && (
                      <button type="button" className="btn btn-doux btn-sm" onClick={() => surRevoquer(c)} aria-label={`Révoquer la clé ${c.label}`}>
                        Révoquer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/** Le journal des accès (qui, quand, avant → après, raison), le plus récent d'abord. */
export function JournalAcces({ etat, recharger }: { etat: Chargement<EntreeJournalApi>; recharger: () => void }) {
  return (
    <>
      <h3 className="fiche-titre"><Icone nom="history" /> Journal des accès API</h3>
      {etat.etat === 'chargement' && <div className="sk" aria-busy="true" aria-label="Chargement du journal" />}
      {etat.etat === 'erreur' && (
        <p className="encart encart-alerte" role="alert">
          <Icone nom="alert" /> {etat.message}{' '}
          <button type="button" className="lien" onClick={recharger}>Réessayer</button>
        </p>
      )}
      {etat.etat === 'pret' && etat.lignes.length === 0 && <p className="discret-t">Aucun changement d'accès API enregistré.</p>}
      {etat.etat === 'pret' && etat.lignes.length > 0 && (
        <ol className="journal">
          {etat.lignes.map((e: EntreeJournalApi) => (
            <li key={e.id}>
              <div className="journal-tete">
                <b>{decrireJournalApi(e)}</b>
                <span className="discret-t">{dateHeure(e.created_at)}</span>
              </div>
              <div className="discret-t">{libelleAuteurApi(e)}</div>
              {e.note && <p className="journal-note">{e.note}</p>}
            </li>
          ))}
        </ol>
      )}
    </>
  )
}

const ouiNon = (v: boolean) => (v ? 'Ouvert' : 'Fermé')

/** Champ « raison » partagé par les deux confirmations : obligatoire, 500 caractères au plus. */
function ChampRaison({ id, valeur, surChange, erreur }: { id: string; valeur: string; surChange: (v: string) => void; erreur: string }) {
  return (
    <div className="champ">
      <label htmlFor={id}>Raison du changement <b aria-hidden="true">*</b></label>
      <textarea
        id={id} rows={3} value={valeur} required maxLength={600}
        placeholder="Ex. Contrat API signé le 12/10/2026, essais autorisés"
        onChange={(e) => surChange(e.target.value)}
        aria-invalid={Boolean(erreur)}
        data-focus-initial
      />
      <span className="aide">Obligatoire (500 caractères au plus), conservée dans le journal avec votre nom et la date.</span>
      {erreur && <span className="erreur" role="alert">{erreur}</span>}
    </div>
  )
}

/**
 * Confirmation d'un changement d'accès : récapitulatif avant / après + raison obligatoire.
 * Les DEUX drapeaux partent ensemble ; celui qui ne change pas garde sa valeur. `enregistrer` appelle la RPC
 * de l'organisation ou celle du client API.
 */
export function ModaleChangementAcces({
  nom, avant, apres, enregistrer, surFermer, surFait,
}: {
  nom: string
  avant: Drapeaux
  apres: Drapeaux
  enregistrer: EnregistrerAcces
  surFermer: () => void
  surFait: () => Promise<void>
}) {
  const [note, setNote] = useState('')
  const [erreur, setErreur] = useState('')
  const [echec, setEchec] = useState('')
  const [enCours, setEnCours] = useState(false)

  async function confirmer() {
    if (enCours) return
    const v = validerNoteAcces(note)
    if (!v.ok) { setErreur(v.message); return }
    setEnCours(true)
    setErreur('')
    setEchec('')
    try {
      await enregistrer({ sandbox: apres.sandbox, production: apres.production, note: v.note })
    } catch (e) {
      setEchec(messageAccesApi(e))
      setEnCours(false)
      return
    }
    // L'écriture est faite : un échec de rechargement ne doit pas passer pour un échec d'écriture.
    await surFait()
    setEnCours(false)
    surFermer()
  }

  return (
    <Modale
      titre="Modifier l'accès API"
      sousTitre={nom}
      surFermer={surFermer}
      pied={
        <>
          <button type="button" className="btn btn-doux" onClick={surFermer} disabled={enCours}>Annuler</button>
          <button type="button" className="btn btn-plein" onClick={() => void confirmer()} disabled={enCours}>
            {enCours ? 'Enregistrement…' : "Confirmer l'enregistrement"}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void confirmer() }} noValidate>
        <p className="encart"><Icone nom="alert" /> Le changement est immédiat et journalisé. Seul un administrateur de la plateforme peut le faire.</p>
        <table className="recap">
          <thead><tr><th /><th>Avant</th><th>Après</th></tr></thead>
          <tbody>
            <tr><th scope="row">Accès de test</th><td>{ouiNon(avant.sandbox)}</td><td className="fort">{ouiNon(apres.sandbox)}</td></tr>
            <tr><th scope="row">Accès de production</th><td>{ouiNon(avant.production)}</td><td className="fort">{ouiNon(apres.production)}</td></tr>
          </tbody>
        </table>
        <ChampRaison id="api-raison" valeur={note} surChange={(v) => { setNote(v); setErreur('') }} erreur={erreur} />
        {echec && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {echec}</p>}
      </form>
    </Modale>
  )
}

/** Confirmation de la révocation d'une clé (irréversible) : raison obligatoire. */
export function ModaleRevocation({
  cle, nom, revoquer, surFermer, surFait,
}: {
  cle: CleApi
  nom: string
  revoquer: (note: string) => Promise<void>
  surFermer: () => void
  surFait: () => Promise<void>
}) {
  const [note, setNote] = useState('')
  const [erreur, setErreur] = useState('')
  const [echec, setEchec] = useState('')
  const [enCours, setEnCours] = useState(false)

  async function confirmer() {
    if (enCours) return
    const v = validerNoteAcces(note)
    if (!v.ok) { setErreur(v.message); return }
    setEnCours(true)
    setErreur('')
    setEchec('')
    try {
      await revoquer(v.note)
    } catch (e) {
      setEchec(messageAccesApi(e))
      setEnCours(false)
      return
    }
    await surFait()
    setEnCours(false)
    surFermer()
  }

  return (
    <Modale
      titre="Révoquer la clé"
      sousTitre={nom}
      surFermer={surFermer}
      pied={
        <>
          <button type="button" className="btn btn-doux" onClick={surFermer} disabled={enCours}>Annuler</button>
          <button type="button" className="btn btn-danger" onClick={() => void confirmer()} disabled={enCours}>
            {enCours ? 'Révocation…' : 'Révoquer la clé'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void confirmer() }} noValidate>
        <p className="encart encart-alerte">
          <Icone nom="alert" /> La clé « {cle.label} » ({prefixeCle(cle)}, {LIBELLE_MODE_CLE[cle.mode].toLowerCase()}) cessera
          immédiatement de fonctionner. Une révocation ne se défait pas : le client devra créer une nouvelle clé.
        </p>
        <ChampRaison id="api-revocation-raison" valeur={note} surChange={(v) => { setNote(v); setErreur('') }} erreur={erreur} />
        {echec && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {echec}</p>}
      </form>
    </Modale>
  )
}
